import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { DateRangePicker, FocusedInputShape } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';

import { Button } from '../../../../../../base/components/Button';
import { Icon } from '../../../../../../base/components/Icon';
import { Modal } from '../../../../../../base/components/Modal';
import { useAppDispatch, useIsMobile } from '../../../../../../base/hooks';
import { __t } from '../../../../../../base/utils/t';
import { responsesActions } from '../../../redux/slices/responsesSlice';

import './styles.scss';

interface Props {
    visible: boolean;
    closeHandler: () => void;
    onModalCloseCallback: () => void;
}

export const DatePickerModal = ({ visible, closeHandler, onModalCloseCallback }: Props) => {
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();

    const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);
    const [startDate, setStartDate] = useState<Moment | null>(null);
    const [endDate, setEndDate] = useState<Moment | null>(null);
    const [showError, setShowError] = useState<boolean>(false);

    useEffect(() => {
        return () => {
            setFocusedInput(null);
            setStartDate(null);
            setEndDate(null);
        };
    }, []);

    const dateChange = ({ startDate, endDate }: { startDate: Moment | null; endDate: Moment | null }) => {
        setStartDate(startDate ? startDate?.startOf('day') : null);
        setEndDate(endDate ? endDate?.endOf('day') : null);
    };

    const focusChange = (focusedInput: FocusedInputShape | null) => {
        setShowError(false);
        setFocusedInput(focusedInput);
    };

    const submitHandler = () => {
        if (!startDate || !endDate) {
            setShowError(true);
            return;
        }

        dispatch(
            responsesActions.setFiltersDates({
                dateFrom: startDate?.toDate(),
                dateTo: endDate?.toDate(),
            }),
        );
        closeHandler();
    };

    return (
        <Modal
            visible={visible}
            visibilityHandler={closeHandler}
            onCloseCallback={onModalCloseCallback}
            className='responses-table-date-range-input-modal'
            containerClassName='responses-table-date-range-input-modal__container'
        >
            <h4 className='responses-table-date-range-input-modal__title'>{__t('Показати відгуки за період')}</h4>
            <DateRangePicker
                startDate={startDate}
                startDateId='your_unique_start_date_id'
                endDate={endDate}
                endDateId='your_unique_end_date_id'
                maxDate={moment().endOf('day')}
                isOutsideRange={(day) => moment().diff(day) < 0}
                initialVisibleMonth={() => moment().subtract(1, 'months')}
                onDatesChange={dateChange}
                focusedInput={focusedInput}
                onFocusChange={focusChange}
                startDatePlaceholderText={__t('Від')}
                endDatePlaceholderText={__t('По')}
                hideKeyboardShortcutsPanel={true}
                minimumNights={0}
                readOnly={true}
                numberOfMonths={isMobile ? 1 : 2}
                customArrowIcon={
                    <span className='responses-table-date-range-input-modal__datepicker-separator'>—</span>
                }
                navPrev={
                    <Icon
                        icon='chevron-left-regular'
                        className='responses-table-date-range-input-modal__datepicker-prev-button'
                        color='info'
                    />
                }
                navNext={
                    <Icon
                        icon='chevron-right-regular'
                        className='responses-table-date-range-input-modal__datepicker-next-button'
                        color='info'
                    />
                }
            />
            {showError && (
                <p className='responses-table-date-range-input-modal__error'>{__t('Оберіть період для пошуку')}</p>
            )}
            <Button onClick={submitHandler} className='responses-table-date-range-input-modal__button'>
                {__t('Пошук')}
            </Button>
        </Modal>
    );
};
